import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getDocumentName } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFromLatLonToAddressForm } from '../../../forms/Address/hooks/useFromLatLonToAddressForm.hook';
import { useCreateOrganizationMutation } from '../../../forms/ThirdParty/hooks/useCreateOrganizationMutation.hook';
import { useLegalStatusesQuery } from '../../../forms/ThirdParty/hooks/useLegalStatusesQuery.hook';
import { useNafEntriesQuery } from '../../../forms/ThirdParty/hooks/useNafEntriesQuery.hook';
import { useOrganizationInitialValuesFactory } from '../../../forms/ThirdParty/hooks/useOrganizationInitialValuesFactory.hook';
import { useUpdateOrganizationMembershipsMutation } from '../../../forms/ThirdParty/hooks/useUpdateOrganizationMembershipsMutation.hook';
import { organizationMembershipForm2ApiMapper } from '../../../forms/ThirdParty/mappers/organization-membership.form2api.mapper';
import { organizationForm2ApiMapper } from '../../../forms/ThirdParty/mappers/organization.form2api.mapper';
import { ThirdPartiesCmp_SomeOrganizations_QueryDocument } from '../../../components/ThirdParties/queries/some-organizations.query';

import { resolveCreationErrorMessage } from '../utils/resolve-creation-error-message.util';
import { buildOrganizationName } from '../mappers/establishment-to-organization-input.mapper';
import {
  buildIdentifier,
  buildNafIdsByCode,
  resolveAddresses,
  resolveLegalStatusId,
  resolveTradingName,
} from '../mappers/organization-search-result.mapper';
import type { ActiveMembership } from '../types/active-membership.type';
import type { CreationStatus } from '../types/creation-status.type';
import type {
  OrganizationSearchEstablishment,
  OrganizationSearchResult,
} from '../types/organization-search-result.type';

import { useRetryableCreation } from './useRetryableCreation.hook';

export interface EstablishmentSettledOutcome {
  readonly membershipsApplied?: boolean;
  readonly organizationId?: Id;
  readonly status: 'fulfilled' | 'rejected';
}

interface Params {
  readonly activeMemberships: readonly ActiveMembership[];
  readonly canStart: boolean;
  readonly establishment: Readonly<OrganizationSearchEstablishment>;
  readonly isHeadOffice: boolean;
  readonly motherId: Id | null;
  readonly onSettled?: (outcome: Readonly<EstablishmentSettledOutcome>) => void;
  readonly result: Readonly<OrganizationSearchResult>;
}

interface Result {
  readonly errorMessage?: string;
  readonly membershipsApplied?: boolean;
  readonly organizationId?: Id;
  readonly retry: () => void;
  readonly status: CreationStatus;
}

const toFiniteNumber = (value: string | null | undefined) => {
  const parsed = value ? Number(value) : NaN;

  return Number.isFinite(parsed) ? parsed : undefined;
};

export const useEstablishmentOrganizationCreation = ({
  activeMemberships,
  canStart,
  establishment,
  isHeadOffice,
  motherId,
  onSettled,
  result,
}: Readonly<Params>): Result => {
  const queryClient = useQueryClient();
  const { addressTypesCodeMap } = useReferencesContext();

  const { data: nafEntriesData } = useNafEntriesQuery();
  const { data: legalStatusesData } = useLegalStatusesQuery();
  const legalStatuses =
    legalStatusesData?.findAllActiveLegalStatuses ?? EMPTY_ARRAY;

  const nafIdsByCode = useMemo(
    () =>
      buildNafIdsByCode(nafEntriesData?.findAllActiveNafEntries ?? EMPTY_ARRAY),
    [nafEntriesData],
  );

  const latitude = toFiniteNumber(establishment.latitude);
  const longitude = toFiniteNumber(establishment.longitude);
  const hasCoordinates = latitude !== undefined && longitude !== undefined;

  const {
    data: resolvedAddressForm,
    isError: addressIsError,
    isLoading: addressIsLoading,
  } = useFromLatLonToAddressForm({
    latLon: hasCoordinates ? { lat: latitude, lon: longitude } : undefined,
    enabled: canStart && hasCoordinates,
  });

  const isAddressReady = !hasCoordinates || addressIsError || !addressIsLoading;
  const addresses = resolveAddresses(establishment, {
    addressIsError,
    addressTypeId: isHeadOffice
      ? addressTypesCodeMap.HeadOffice._id
      : addressTypesCodeMap.SecondaryOffice._id,
    hasCoordinates,
    resolvedAddressForm,
  });

  const { initialValues } = useOrganizationInitialValuesFactory({
    isInternal: false,
    initialValues: {
      name: buildOrganizationName(result, establishment),
      tradingName: resolveTradingName(result, establishment),
      identifier: buildIdentifier(result, establishment),
      nafId: result.activite_principale_naf25
        ? nafIdsByCode.get(result.activite_principale_naf25)
        : undefined,
      legalStatusId: resolveLegalStatusId(result, { legalStatuses }),
      birthDate: result.date_creation ?? undefined,
      vatNumber: result.tva?.[0] ?? undefined,
      addresses,
      activeMemberships,
      motherId: motherId ?? undefined,
    },
  });

  const { mutateAsync: createOrganizationMutation } =
    useCreateOrganizationMutation();
  const { mutateAsync: updateOrganizationMemberships } =
    useUpdateOrganizationMembershipsMutation();

  const [state, setState] = useState<Omit<Result, 'retry'>>({
    status: 'pending',
  });

  const createdOrganizationIdRef = useRef<Id | null>(null);

  const retryMemberships = async (organizationId: Id) => {
    try {
      await updateOrganizationMemberships({
        activeMemberships: activeMemberships.map(
          organizationMembershipForm2ApiMapper,
        ),
        organizationId,
      });
      setState({ membershipsApplied: true, organizationId, status: 'success' });
    } catch (error) {
      setState({
        errorMessage: resolveCreationErrorMessage(error),
        membershipsApplied: false,
        organizationId,
        status: 'success',
      });
    }
  };

  const createOrganization = async () => {
    setState({ status: 'pending' });

    try {
      const { createOrganization: createdOrganization } =
        await createOrganizationMutation({
          organization: organizationForm2ApiMapper(initialValues),
        });

      createdOrganizationIdRef.current = createdOrganization._id;

      let membershipsApplied = true;
      if (activeMemberships.length > 0) {
        try {
          await updateOrganizationMemberships({
            activeMemberships: activeMemberships.map(
              organizationMembershipForm2ApiMapper,
            ),
            organizationId: createdOrganization._id,
          });
        } catch (error) {
          membershipsApplied = false;
          console.error(
            `Échec de l'application des membres à l'organisation ${createdOrganization._id.toString()}`,
            error,
          );
        }
      }

      setState({
        membershipsApplied,
        organizationId: createdOrganization._id,
        status: 'success',
      });

      await queryClient.invalidateQueries({
        queryKey: [
          getDocumentName(ThirdPartiesCmp_SomeOrganizations_QueryDocument),
        ],
      });

      onSettled?.({
        membershipsApplied,
        organizationId: createdOrganization._id,
        status: 'fulfilled',
      });
    } catch (error) {
      setState({
        errorMessage: resolveCreationErrorMessage(error),
        status: 'error',
      });

      onSettled?.({ status: 'rejected' });
    }
  };

  const { retry } = useRetryableCreation({
    canStart: canStart && isAddressReady,
    perform: () =>
      createdOrganizationIdRef.current
        ? retryMemberships(createdOrganizationIdRef.current)
        : createOrganization(),
  });

  return { ...state, retry };
};
